import type { EvidenceView } from '@/api/types';
import { ERASED_LABEL, isErased } from '@/lib/erased';

/**
 * The case detail's evidence model (OL-26). One viewer per capability family, the closed list of
 * twelve, never one per check slug, so a new flow template renders with no console change. The
 * family comes from the evidence's provenance token (`screening.v1/mock-openscreen`); the data room
 * is a surface rather than a family, so its evidence kinds map onto the family they feed. Anything
 * unrecognised falls to the generic viewer rather than disappearing.
 */
export type FamilyId =
  | 'screening'
  | 'identity-verification'
  | 'document-analysis'
  | 'biometric'
  | 'registry-lookup'
  | 'risk-scoring'
  | 'communication'
  | 'e-sign'
  | 'video-kyc'
  | 'banking-verification'
  | 'forms'
  | 'transaction-monitoring';

export interface FamilyViewer {
  label: string;
  /** Result fields worth leading with, in order, by leaf name. Everything else follows. */
  lead: string[];
}

export const FAMILY_VIEWERS: Record<FamilyId, FamilyViewer> = {
  screening: { label: 'Screening', lead: ['hits', 'matchCount', 'score', 'lists', 'subject_name'] },
  'identity-verification': {
    label: 'Identity verification',
    lead: ['verification_method', 'verificationMethod', 'verified', 'maskedNumber', 'subject_name', 'scheme', 'value'],
  },
  'document-analysis': { label: 'Documents', lead: ['documentId', 'mediaType', 'originalName', 'tampered', 'confidence'] },
  biometric: { label: 'Biometrics', lead: ['liveness', 'faceMatch', 'score', 'mediaType'] },
  'registry-lookup': { label: 'Registry lookups', lead: ['registeredName', 'status', 'registry', 'number'] },
  'risk-scoring': { label: 'Risk scoring', lead: ['band', 'score', 'reasons'] },
  communication: { label: 'Communication', lead: ['channel', 'maskedContact', 'verifiedAt', 'delivered'] },
  'e-sign': { label: 'E-sign', lead: ['signMethod', 'status', 'signedAt', 'signatories'] },
  'video-kyc': { label: 'Video KYC', lead: ['verdict', 'sessionId', 'recordingRegion'] },
  'banking-verification': { label: 'Banking', lead: ['mode', 'ifsc', 'vpa', 'accountHolderName', 'nameMatch', 'verified'] },
  forms: { label: 'Forms and consent', lead: ['purpose', 'declarationVersion', 'locale', 'acceptedAt', 'fields'] },
  'transaction-monitoring': { label: 'Transaction monitoring', lead: ['alerts', 'score', 'scenario'] },
};

export const FAMILY_IDS = Object.keys(FAMILY_VIEWERS) as FamilyId[];

/** Data-room evidence kinds, by the family each one feeds. */
const ROOM_KIND_FAMILY: Record<string, FamilyId> = {
  DATAROOM_DOCUMENT: 'document-analysis',
  DATAROOM_FORM_RESPONSE: 'forms',
  CONSENT: 'forms',
  DECLARED_PARTIES: 'forms',
  SELFIE: 'biometric',
  LIVENESS_VIDEO: 'biometric',
  VERIFIED_CONTACT: 'communication',
  DECLARED_IDENTIFIER: 'identity-verification',
  AADHAAR_EKYC: 'identity-verification',
  DECLARED_BANK_ACCOUNT: 'banking-verification',
};

const DOCUMENT_KINDS = new Set(['DATAROOM_DOCUMENT', 'SELFIE', 'LIVENESS_VIDEO']);
const QUESTIONNAIRE_KINDS = new Set(['DATAROOM_FORM_RESPONSE', 'QUESTIONNAIRE_RESPONSE', 'DECLARED_PARTIES', 'CONSENT']);

/** Is this what the applicant declared about a party, or a check run on such a party? Never a register's word. */
export function isDeclaredParty(e: EvidenceView): boolean {
  if (e.redacted) return false;
  const body = e.detail?.detail;
  return evidenceKind(e) === 'DECLARED_PARTIES'
    || (typeof body === 'object' && body !== null && (body as Record<string, unknown>).partySource === 'DECLARED');
}

export function evidenceKind(e: EvidenceView): string | null {
  const kind = e.detail?.kind;
  return typeof kind === 'string' ? kind : null;
}

/** The family a record belongs to, or null for the generic viewer. */
export function familyOf(e: EvidenceView): FamilyId | null {
  const kind = evidenceKind(e);
  if (kind && ROOM_KIND_FAMILY[kind]) return ROOM_KIND_FAMILY[kind];
  const token = (e.source ?? '').split('.v')[0];
  return (FAMILY_IDS as string[]).includes(token) ? (token as FamilyId) : null;
}

/** A step's full result, kept beside its claim. It belongs in the evidence list, not among documents. */
const RESULT_KIND = 'CAPABILITY_RESULT';

/** Does a file sit behind this record? The room names it on the claim, whatever kind the step asked for. */
export function hasStoredFile(e: EvidenceView): boolean {
  if (e.redacted || evidenceKind(e) === RESULT_KIND) return false;
  const body = e.detail?.detail;
  return typeof body === 'object' && body !== null && typeof (body as Record<string, unknown>).documentId === 'string';
}

export const isDocument = (e: EvidenceView) =>
  evidenceKind(e) !== RESULT_KIND
  && (familyOf(e) === 'document-analysis' || DOCUMENT_KINDS.has(evidenceKind(e) ?? '') || hasStoredFile(e));

export type PreviewKind = 'pdf' | 'image';

const PREVIEW_KINDS: Record<string, PreviewKind> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'image',
  'image/png': 'image',
};

/**
 * How the case page may draw a file, from the type the server served it as. A closed list, so SVG, HTML and
 * anything new get the download. TIFF and HEIC are not on it because browsers do not draw them reliably.
 */
export function previewKindOf(contentType: string): PreviewKind | null {
  return PREVIEW_KINDS[contentType.split(';')[0].trim().toLowerCase()] ?? null;
}

export const isQuestionnaire = (e: EvidenceView) => QUESTIONNAIRE_KINDS.has(evidenceKind(e) ?? '');

/** Was this record produced by this run? Fabric keys are `<runId>/<nodeId>`; room keys are not. */
export function fromRun(e: EvidenceView, runId: string): boolean {
  const key = e.detail?.idempotency_key;
  return typeof key === 'string' && key.startsWith(`${runId}/`);
}

export function groupByFamily(evidence: EvidenceView[]): Array<{ family: FamilyId | null; items: EvidenceView[] }> {
  const groups = new Map<FamilyId | null, EvidenceView[]>();
  for (const e of evidence) {
    const f = familyOf(e);
    groups.set(f, [...(groups.get(f) ?? []), e]);
  }
  const known = FAMILY_IDS.filter((f) => groups.has(f)).map((family) => ({ family, items: groups.get(family)! }));
  return groups.has(null) ? [...known, { family: null, items: groups.get(null)! }] : known;
}

const VERHOEFF_D = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];
const VERHOEFF_P = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

function verhoeffValid(digits: string): boolean {
  let c = 0;
  for (let i = 0; i < digits.length; i++) {
    c = VERHOEFF_D[c][VERHOEFF_P[i % 8][digits.charCodeAt(digits.length - 1 - i) - 48]];
  }
  return c === 0;
}

const TWELVE_DIGITS = /(?<![0-9])[2-9][0-9]{3}[ -]?[0-9]{4}[ -]?[0-9]{4}(?![0-9])/g;

/**
 * Last line of defence (REG-IN-40). The server never stores an Aadhaar number, but a provider or an
 * older record might carry one, and this page must not be what displays it. Anything shaped like one
 * with a valid check digit is masked to its last four digits.
 */
export function maskAadhaar(text: string): string {
  return text.replace(TWELVE_DIGITS, (m) => {
    const digits = m.replace(/[ -]/g, '');
    return verhoeffValid(digits) ? `XXXX-XXXX-${digits.slice(-4)}` : m;
  });
}

const ENVELOPE_KEYS = new Set(['kind', 'source', 'captured_at', 'idempotency_key', 'sessionId', 'taskId']);

export interface EvidenceField {
  key: string;
  value: string;
}

function leaves(value: unknown, path: string, out: EvidenceField[], depth: number): void {
  if (value == null) return;
  if (isErased(value)) {
    out.push({ key: path, value: ERASED_LABEL });
    return;
  }
  if (Array.isArray(value) && value.every((v) => v === null || typeof v !== 'object')) {
    if (value.length > 0) out.push({ key: path, value: maskAadhaar(value.join(', ')) });
    return;
  }
  if (typeof value === 'object' && depth < 4) {
    const entries = Array.isArray(value)
      ? value.map((v, i) => [String(i), v] as const)
      : Object.entries(value as Record<string, unknown>);
    for (const [k, v] of entries) leaves(v, path ? `${path}.${k}` : k, out, depth + 1);
    return;
  }
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  out.push({ key: path, value: maskAadhaar(text) });
}

/**
 * The fields a viewer shows, lead fields first. A redacted shell shows none: after erasure the
 * record is `{kind, redacted}`, and only that fact is displayed.
 */
export function evidenceFields(e: EvidenceView, viewer: FamilyViewer | null): EvidenceField[] {
  if (e.redacted || !e.detail) return [];
  const body = (e.detail.detail ?? e.detail) as Record<string, unknown>;
  if (isErased(body)) return [{ key: 'record', value: ERASED_LABEL }];
  const out: EvidenceField[] = [];
  // A declared-parties record's result repeats its parties, in the shape a flow reads.
  const declaredParties = evidenceKind(e) === 'DECLARED_PARTIES';
  for (const [k, v] of Object.entries(body)) {
    if (!ENVELOPE_KEYS.has(k) && !(declaredParties && k === 'result')) leaves(v, k, out, 0);
  }
  const lead = viewer?.lead ?? [];
  const rank = (f: EvidenceField) => {
    const i = lead.indexOf(f.key.split('.').pop() ?? f.key);
    return i === -1 ? lead.length : i;
  };
  return out.map((f, i) => ({ f, i })).sort((a, b) => rank(a.f) - rank(b.f) || a.i - b.i).map(({ f }) => f);
}
